import React, {useEffect, useMemo, useState} from 'react';
import {Form, Field, useForm, useFormState} from 'react-final-form';
import {useSelector} from 'react-redux';
import SearchableSelect from '../../common/SearchableSelect';
import {composeValidators, required, email, limit, phoneNumber} from '@form/helpers/ValidateHelper';

function ResumeInputField({input, className, placeholder, autoComplete, inputProps, disabled, meta: {touched, error, dirtySinceLastSubmit, submitError}}) {
    const hasError = (touched && error) || (!dirtySinceLastSubmit && submitError);

    return <input
        {...input}
        {...inputProps}
        className={className + (hasError ? ' resume-auth__input--error' : '')}
        placeholder={placeholder}
        autoComplete={autoComplete}
        disabled={disabled}
    />;
}

function ResumeError({name}) {
    return <Field
        name={name}
        subscribe={{touched: true, error: true, submitError: true, dirtySinceLastSubmit: true}}
        render={({meta: {touched, error, submitError, dirtySinceLastSubmit}}) => {
            const visibleError = ((touched && error) || (!dirtySinceLastSubmit && submitError))
                ? (error || submitError)
                : '';
            return <div className="resume-auth__error">{visibleError}</div>;
        }}
    />;
}

function PhoneCodeField({countryOptions}) {
    const form = useForm();
    const {values} = useFormState({subscription: {values: true}});

    return (
        <SearchableSelect
            options={countryOptions}
            value={values.phone?.code || ''}
            placeholder="+1"
            onSelect={(val) => form.change('phone.code', val)}
            getLabel={o => o.label}
            renderPrefix={o => (
                <img src={o.flag} alt="" className="resume-select-dropdown__flag" />
            )}
            renderOption={o => (
                <span className="resume-phone-option">
                    <img src={o.flag} alt="" className="resume-phone-option__flag" />
                    <span className="resume-phone-option__code">{o.label}</span>
                    <span className="resume-phone-option__name">{o.name}</span>
                </span>
            )}
        />
    );
}

function ResumeRegisterForm({onSubmit}) {
    const {ipIsoCode} = useSelector(state => state.user);
    const authOptions = useSelector(state => state.authOptions.data);
    const [showSubscribeField, setShowSubscribeField] = useState(false);

    useEffect(() => {
        setShowSubscribeField(ipIsoCode !== 'us');
    }, [ipIsoCode]);

    const countryOptions = useMemo(() => {
        if (!authOptions.countries) return [];
        return authOptions.countries.map(option => ({
            value: option.value,
            label: '+' + option.code,
            name: option.label,
            iso: option.iso,
            flag: `/_core/assets/common/css/flags/4x3/${option.iso}.svg`,
        }));
    }, [authOptions.countries]);

    const initialValues = useMemo(() => ({
        phone: {
            code: ipIsoCode || '',
        },
        subscribe: true,
    }), [ipIsoCode]);

    return (
        <Form onSubmit={onSubmit} initialValues={initialValues}>
            {({handleSubmit, submitting}) => (
                <form onSubmit={handleSubmit} className="resume-auth__form" noValidate>

                    <div className="resume-auth__form-group">
                        <label className="resume-auth__label">
                            Email <span className="resume-auth__required">*</span>
                        </label>
                        <Field
                            name="email"
                            type="email"
                            component={ResumeInputField}
                            placeholder="e.g. Email@address.com"
                            className="resume-auth__input"
                            autoComplete="email"
                            inputProps={{
                                inputMode: 'email',
                                autoCorrect: 'off',
                                spellCheck: 'false',
                                autoCapitalize: 'none',
                            }}
                            validate={composeValidators(
                                required('Please specify type email'),
                                email('Please provide a valid email'),
                                limit('Maximum {limit} characters allowed. You\'ve used {count}.', 320)
                            )}
                        />
                        <ResumeError name="email" />
                    </div>

                    <div className="resume-auth__form-group">
                        <label className="resume-auth__label">
                            Contact number <span className="resume-auth__required">*</span>
                        </label>
                        <div className="resume-auth__phone-row">
                            <div className="resume-auth__phone-code">
                                <PhoneCodeField countryOptions={countryOptions} />
                            </div>
                            <Field
                                name="phone.number"
                                component={ResumeInputField}
                                className="resume-auth__input resume-auth__phone-number"
                                placeholder="Phone number"
                                inputProps={{
                                    inputMode: 'tel',
                                }}
                                validate={composeValidators(
                                    required('Please provide a valid phone number'),
                                    phoneNumber('Please provide a valid phone number')
                                )}
                            />
                        </div>
                        <ResumeError name="phone.number" />
                    </div>

                    {showSubscribeField && (
                        <div className="resume-auth__form-group">
                            <label className="resume-auth__checkbox">
                                <Field
                                    name="subscribe"
                                    type="checkbox"
                                    component="input"
                                    className="resume-auth__checkbox-input"
                                />
                                <span className="resume-auth__checkbox-mark" />
                                <span className="resume-auth__checkbox-text">
                                    I want to receive news and special offers
                                </span>
                            </label>
                        </div>
                    )}

                    <div className="resume-auth__form-actions">
                        <button
                            type="submit"
                            className="resume-auth__btn"
                            disabled={submitting}
                        >
                            {submitting ? 'Creating account...' : 'Create your account'}
                        </button>
                    </div>
                </form>
            )}
        </Form>
    );
}

export default ResumeRegisterForm;
